import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Divider,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Building2,
  Folder,
  Folders,
  GraduationCap,
  ListEnd,
  Square,
  SquareCheck,
  TextAlignJustify,
  User,
  Users,
} from "lucide-react";
import BadgeBoxCst from "@components/BadgeBoxCst";
import { truncateMiddle } from "@utils/utilsString";
import {
  ListItemCardProps,
  ListItemCardSubtitleSegment,
  ListItemCardMetrics,
  ListItemCardDetails,
} from "./types";
import {
  CardRow,
  CheckboxArea,
  IconArea,
  InfoArea,
  HeadingBlock,
  TitleRow,
  MetricsRow,
  MetricItem,
  MetricClickable,
  MetricSeparator,
  MetricsWrapper,
  SubtitleRow,
  SubtitleItem,
  RightArea,
  BadgesRow,
  ActionsRow,
  IconActionBtn,
  IconActionGhostBtn,
  DetailsRow,
  DetailsWrapper,
  DetailsContainer,
  Bottom,
  Top,
  MandatorySelectWrapper,
} from "./style";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { isClassroom } from "./utils";
import MoreActionsMenu from "./MoreActionsMenu";

const SUBTITLE_CODE_MAX_LENGTH = 18;

const ListItemCardCst: React.FC<ListItemCardProps> = ({
  icon,
  title,
  titleAdornment,
  subtitle,
  metrics,
  details,
  detailsVariant = "default",
  badges,
  actions,
  moreActions,
  moreActionsTestId,
  dragHandle,
  leftDivider = false,
  selectable = false,
  selected = false,
  selectDisabled = false,
  mandatorySelection,
  mandatorySelected = false,
  onMandatorySelect,
  backgroundVariant = "card",
  onSelect,
  onClick,
  onMetricClick,
  isPublished,
  onPublish,
  onCreateEdition,
  onSeeEditions,
  activityType = null,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("list-item-card");
  const { t: tCommon } = useTranslation("common");

  const showPublishBtn = isPublished === false && !!onPublish;
  const showCreateEditionBtn = !!onCreateEdition;
  const showSeeEditionsBtn = !!onSeeEditions;

  const subtitleSegments = useMemo((): ListItemCardSubtitleSegment[] => {
    if (!subtitle) return [];
    const parts: ListItemCardSubtitleSegment[] = [];
    if (subtitle.id !== undefined) parts.push({ label: `ID: ${subtitle.id}` });
    if (subtitle.externalId)
      parts.push({ label: `Ext. ID: ${subtitle.externalId}` });
    const code = subtitle.code?.trim();
    if (code) {
      const displayCode = truncateMiddle(code, SUBTITLE_CODE_MAX_LENGTH);
      parts.push({
        label: `${t("subtitle.code")}: ${displayCode}`,
        tooltip: displayCode !== code ? code : undefined,
      });
    }
    if (subtitle.activityType) parts.push({ label: subtitle.activityType });
    if (subtitle.status) parts.push({ label: subtitle.status });
    if (subtitle.email) parts.push({ label: subtitle.email });
    // TODO: translations
    if (subtitle.groupId)
      parts.push({ label: `ID Gruppo: ${subtitle.groupId}` });
    if (subtitle.segments)
      parts.push(...subtitle.segments.filter((segment) => !!segment.label));
    return parts;
  }, [subtitle, t]);

  const metricsRows = useMemo(() => {
    if (!metrics) return [];
    const row1: {
      key: keyof ListItemCardMetrics;
      icon: React.ReactNode;
      label: string;
    }[] = [];
    const row2: {
      key: keyof ListItemCardMetrics;
      icon: React.ReactNode;
      label: string;
    }[] = [];

    if (metrics.enrolledUsers !== undefined)
      row1.push({
        key: "enrolledUsers",
        icon: <User size={14} />,
        label: t("metrics.enrolled-users", { count: metrics.enrolledUsers }),
      });
    if (metrics.enrolledGroups !== undefined)
      row1.push({
        key: "enrolledGroups",
        icon: <Users size={14} />,
        label: t("metrics.enrolled-groups", { count: metrics.enrolledGroups }),
      });
    if (metrics.initiatives !== undefined)
      row2.push({
        key: "initiatives",
        icon: <Building2 size={14} />,
        label: t("metrics.initiatives", { count: metrics.initiatives }),
      });
    if (metrics.userInitiatives !== undefined)
      row2.push({
        key: "userInitiatives",
        icon: <Building2 size={14} />,
        label: t("metrics.user-initiatives", {
          count: metrics.userInitiatives,
        }),
      });
    if (metrics.dynamicGroups !== undefined)
      row2.push({
        key: "dynamicGroups",
        icon: <ListEnd size={14} />,
        label: t("metrics.dynamic-groups", { count: metrics.dynamicGroups }),
      });
    if (metrics.staticGroups !== undefined)
      row2.push({
        key: "staticGroups",
        icon: <TextAlignJustify size={14} />,
        label: t("metrics.static-groups", { count: metrics.staticGroups }),
      });
    if (metrics.learningObjects !== undefined)
      row2.push({
        key: "learningObjects",
        icon: <GraduationCap size={14} />,
        label: t("metrics.learning-objects", {
          count: metrics.learningObjects,
        }),
      });
    if (metrics.containedActivities !== undefined)
      row2.push({
        key: "containedActivities",
        icon: <GraduationCap size={14} />,
        label: t("metrics.contained-activities", {
          count: metrics.containedActivities,
        }),
      });
    if (metrics.courses !== undefined)
      row2.push({
        key: "courses",
        icon: <Folder size={14} />,
        label: t("metrics.courses", { count: metrics.courses }),
      });
    if (metrics.containedCourses !== undefined)
      row2.push({
        key: "containedCourses",
        icon: <Folder size={14} />,
        label: t("metrics.contained-courses", {
          count: metrics.containedCourses,
        }),
      });
    if (metrics.paths !== undefined)
      row2.push({
        key: "paths",
        icon: <Folders size={14} />,
        label: t("metrics.paths", { count: metrics.paths }),
      });

    return [row1, row2].filter((row) => row.length > 0);
  }, [metrics, t]);

  const isDetailedVariant = detailsVariant === "detailed";

  const Details = (details: ListItemCardDetails) => {
    if (activityType && isClassroom(activityType)) {
      return (
        <>
          <DetailsRow detailed={isDetailedVariant}>
            {tCommon("editionId")}: {subtitle?.sessionIdEnrolled ?? "-"}
          </DetailsRow>
          <DetailsWrapper activityType={activityType}>
            <DetailsRow detailed={isDetailedVariant}>{details.row1}</DetailsRow>
            {details.row2 && (
              <>
                <DetailsRow detailed={isDetailedVariant}>|</DetailsRow>
                <DetailsRow detailed={isDetailedVariant}>
                  {details.row2}
                </DetailsRow>
              </>
            )}
          </DetailsWrapper>
        </>
      );
    }
    return (
      <DetailsWrapper>
        <DetailsRow detailed={isDetailedVariant}>{details.row1}</DetailsRow>
        {details.row2 && (
          <DetailsRow detailed={isDetailedVariant}>{details.row2}</DetailsRow>
        )}
      </DetailsWrapper>
    );
  };

  return (
    <CardRow
      selected={selected}
      backgroundVariant={backgroundVariant}
      onClick={onClick}
      sx={{ cursor: onClick ? "pointer" : "default" }}
    >
      {dragHandle}

      {(selectable || leftDivider) && (
        <>
          {selectable && (
            <CheckboxArea>
              <Checkbox
                checked={selected}
                disabled={selectDisabled}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelect?.(!selected);
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: theme.palette.divider,
                  "&.Mui-checked": {
                    color: theme.palette.primary.main,
                  },
                  padding: "4px",
                }}
              />
            </CheckboxArea>
          )}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: theme.palette.divider }}
          />
        </>
      )}

      <InfoArea>
        <Top>
          <HeadingBlock>
            <TitleRow>
              {icon && <IconArea>{icon}</IconArea>}
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  lineHeight: 1.3,
                  overflowWrap: "anywhere",
                }}
              >
                {title}
              </Typography>
              {titleAdornment}
            </TitleRow>

            {subtitleSegments.length > 0 && (
              <SubtitleRow>
                {subtitleSegments.map((segment, index) => {
                  const item = (
                    <SubtitleItem>
                      {segment.icon}
                      <span>{segment.label}</span>
                    </SubtitleItem>
                  );
                  return (
                    <React.Fragment key={index}>
                      {index > 0 && <span>|</span>}
                      {segment.tooltip ? (
                        <Tooltip title={segment.tooltip}>{item}</Tooltip>
                      ) : (
                        item
                      )}
                    </React.Fragment>
                  );
                })}
              </SubtitleRow>
            )}
            {mandatorySelection && (
              <MandatorySelectWrapper>
                <Checkbox
                  checked={mandatorySelected}
                  icon={<Square size={18} />}
                  checkedIcon={<SquareCheck size={18} />}
                  onChange={(_, checked) => onMandatorySelect?.(checked)}
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    padding: 0,
                    color: theme.palette.divider,
                    "&.Mui-checked": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
                <Typography sx={{ fontSize: "14px" }}>
                  {t("mandatory-content")}
                </Typography>
              </MandatorySelectWrapper>
            )}
          </HeadingBlock>

          {metricsRows.length > 0 && (
            <MetricsWrapper>
              {metricsRows.map((row, rowIndex) => (
                <MetricsRow key={rowIndex}>
                  {row.map((metric, metricIndex) => (
                    <React.Fragment key={metric.key}>
                      {metricIndex > 0 && <MetricSeparator />}
                      <MetricItem>
                        {metric.icon}
                        {onMetricClick ? (
                          <MetricClickable
                            onClick={(e) => {
                              e.stopPropagation();
                              onMetricClick(metric.key);
                            }}
                          >
                            {metric.label}
                          </MetricClickable>
                        ) : (
                          <span>{metric.label}</span>
                        )}
                      </MetricItem>
                    </React.Fragment>
                  ))}
                </MetricsRow>
              ))}
            </MetricsWrapper>
          )}
        </Top>
        {details && (
          <Bottom detailed={isDetailedVariant}>
            <DetailsContainer detailed={isDetailedVariant}>
              {Details(details)}
            </DetailsContainer>
          </Bottom>
        )}
      </InfoArea>

      <RightArea>
        <BadgesRow>
          {badges?.map((badge, index) => {
            const badgeBox = (
              <BadgeBoxCst
                variant={badge.variant}
                icon={badge.icon}
                sx={badge.sx}
              >
                {badge.label}
              </BadgeBoxCst>
            );
            return badge.tooltip ? (
              <Tooltip key={index} title={badge.tooltip}>
                <span>{badgeBox}</span>
              </Tooltip>
            ) : (
              <React.Fragment key={index}>{badgeBox}</React.Fragment>
            );
          })}
        </BadgesRow>

        {(actions ||
          (moreActions && moreActions.length > 0) ||
          showPublishBtn ||
          showCreateEditionBtn ||
          showSeeEditionsBtn) && (
          <ActionsRow>
            {/* TODO: da implementare quando avremo le bozze */}
            {/* {showPublishBtn && (
              <PublishBtn
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  onPublish?.();
                }}
              >
                {t("publish-button")}
              </PublishBtn>
            )} */}

            {showCreateEditionBtn && (
              <ButtonPrimary
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateEdition?.();
                }}
              >
                {t("create-edition-button")}
              </ButtonPrimary>
            )}

            {showSeeEditionsBtn && (
              <ButtonPrimary
                onClick={(e) => {
                  e.stopPropagation();
                  onSeeEditions?.();
                }}
              >
                {t("see-editions-button")}
              </ButtonPrimary>
            )}

            {actions?.map((action, index) => {
              const ActionButton =
                action.variant === "contained"
                  ? ButtonPrimary
                  : ButtonSecondary;
              return action.iconOnly ? (
                action.variant === "ghost" ? (
                  <IconActionGhostBtn
                    key={index}
                    disabled={action.disabled}
                    aria-label={action.label}
                    title={action.label}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick();
                    }}
                  >
                    {action.icon}
                  </IconActionGhostBtn>
                ) : (
                  <IconActionBtn
                    key={index}
                    disabled={action.disabled}
                    aria-label={action.label}
                    title={action.label}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick();
                    }}
                  >
                    {action.icon}
                  </IconActionBtn>
                )
              ) : (
                <ActionButton
                  key={index}
                  size="sm"
                  disabled={action.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                >
                  {action.label}
                </ActionButton>
              );
            })}

            <MoreActionsMenu
              actions={moreActions}
              triggerTestId={moreActionsTestId}
            />
          </ActionsRow>
        )}
      </RightArea>
    </CardRow>
  );
};

export default ListItemCardCst;
